/**
 * Direct Booking (M2) — a professional PMS booking composer.
 *
 * A large 2-column composer (left: Stay / Guest / Pricing / Payment / Booking
 * details; right: sticky live Booking Summary) that reuses the existing AVOS
 * backend end-to-end — NOTHING here is a second engine:
 *   - Property / Unit / Rate Plan: inventory + Revenue & Rates services.
 *   - Guest: Guest CRM (searchable lookup by name/phone/email, dedup, quick add).
 *   - Pricing + GST: the SAME `valueStay` tax engine as Accounts/Invoices (via
 *     /api/booking/quote and /api/booking/compose) — booking and invoice never
 *     diverge.
 *   - Payments: real invoice payment records (split payments) — no separate
 *     ledger. Payment status is DERIVED from paid vs total.
 *   - Booked By: Staff & Payroll people (sales attribution / commission).
 *   - Inventory + Channex: the authoritative availability engine + the existing
 *     automatic ARI sync (queued on confirm; no manual "Sync" click).
 *
 * Double-booking protection is enforced by the DB exclusion constraint and
 * surfaced here as a clear availability error. SSR-safe: the initial render is a
 * pure loading state; all network / Date reads run in effects or handlers.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import {
  UserMessage,
  toUserMessage,
  type UserMessageModel,
} from "../../components/UserMessage.js";
import {
  apiCancelBooking,
  apiCompose,
  apiConfirmBooking,
  apiCreateGuestFull,
  apiCheckDuplicateGuest,
  apiExpireHolds,
  apiListProperties,
  apiListReservations,
  apiListStaff,
  apiListUnits,
  apiQuote,
  apiSearchGuests,
  asError,
  formatMoney,
  parseMoneyToMinor,
  type ComposeResult,
  type ComposerAddon,
  type ComposerPayment,
  type ExpireHoldsResult,
  type GuestSearchRow,
  type Property,
  type QuoteResult,
  type Reservation,
  type ReservationStatus,
  type StaffOption,
  type Unit,
} from "../services.js";

// ===========================================================================
// Small helpers
// ===========================================================================

function isoDateOffset(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function statusBadgeClass(status: ReservationStatus): string {
  return `booking-state booking-state--${status}`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function prettyDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "UTC" });
}

/** Acquisition sources for a DIRECT/manual booking (never OTA/Channex here). */
const BOOKING_SOURCES: readonly { readonly label: string; readonly sourceType: "direct" | "manual" | "walk_in" }[] = [
  { label: "Direct", sourceType: "direct" },
  { label: "Walk-in", sourceType: "walk_in" },
  { label: "Phone", sourceType: "direct" },
  { label: "WhatsApp", sourceType: "direct" },
  { label: "Email", sourceType: "direct" },
  { label: "Website", sourceType: "direct" },
  { label: "Instagram", sourceType: "direct" },
  { label: "Facebook", sourceType: "direct" },
  { label: "Corporate", sourceType: "manual" },
  { label: "Travel Agent", sourceType: "manual" },
  { label: "Referral", sourceType: "direct" },
  { label: "Other", sourceType: "manual" },
];

const ADDON_PRESETS: readonly { readonly type: string; readonly name: string }[] = [
  { type: "extra_bed", name: "Extra Bed" },
  { type: "breakfast", name: "Breakfast" },
  { type: "airport_transfer", name: "Airport Transfer" },
  { type: "cleaning", name: "Cleaning Fee" },
  { type: "decoration", name: "Decoration" },
  { type: "early_checkin", name: "Early Check-in" },
  { type: "late_checkout", name: "Late Checkout" },
  { type: "other", name: "Other" },
];

const RATE_OVERRIDE_REASONS = [
  "Negotiated rate",
  "Corporate rate",
  "Owner approved",
  "Repeat guest",
  "Special promotion",
  "Other",
] as const;

const PAYMENT_METHODS = ["Cash", "Card", "UPI", "Bank Transfer", "Payment Gateway", "Other"] as const;

interface AddonRow extends ComposerAddon {
  readonly key: string;
}
interface PaymentRow extends ComposerPayment {
  readonly key: string;
}

let keySeq = 0;
function nextKey(): string {
  keySeq += 1;
  return `k${keySeq}`;
}

// ===========================================================================
// Guest search combobox
// ===========================================================================

function GuestCombobox({
  selected,
  onSelect,
  onAddNew,
}: {
  readonly selected: GuestSearchRow | null;
  readonly onSelect: (g: GuestSearchRow) => void;
  readonly onAddNew: (initialQuery: string) => void;
}): ReactElement {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<readonly GuestSearchRow[]>([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let live = true;
    setLoading(true);
    const t = setTimeout(() => {
      apiSearchGuests(q)
        .then((rows) => {
          if (live) {
            setResults(rows);
            setActive(0);
            setLoading(false);
          }
        })
        .catch(() => {
          if (live) {
            setResults([]);
            setLoading(false);
          }
        });
    }, 180);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [q, open]);

  if (selected !== null) {
    return (
      <div className="composer-guest-chip">
        <div className="composer-guest-chip__main">
          <span className="composer-guest-chip__name">{selected.fullName}</span>
          <span className="composer-guest-chip__meta">
            {selected.phone ?? "no phone"}
            {selected.email !== null ? ` · ${selected.email}` : ""}
            {selected.stayCount > 0 ? ` · ${selected.stayCount} stay${selected.stayCount === 1 ? "" : "s"}` : ""}
          </span>
        </div>
        <button
          type="button"
          className="console-btn console-btn--ghost console-btn--sm"
          onClick={() => {
            setQ("");
            onSelect({ id: "", fullName: "", email: null, phone: null, dob: null, city: null, country: null, tags: [], preferences: null, dietary_notes: null, stayCount: 0 });
          }}
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="composer-combobox">
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls="guest-listbox"
        placeholder="Search by name, phone or email…"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, results.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            const g = results[active];
            if (g) onSelect(g);
            setOpen(false);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open ? (
        <ul className="composer-listbox" id="guest-listbox" role="listbox">
          {loading ? <li className="composer-listbox__empty">Searching…</li> : null}
          {!loading && results.length === 0 ? (
            <li className="composer-listbox__empty">No matching guest.</li>
          ) : null}
          {results.map((g, i) => (
            <li
              key={g.id}
              role="option"
              aria-selected={i === active}
              className={`composer-listbox__item${i === active ? " is-active" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(g);
                setOpen(false);
              }}
            >
              <span className="composer-listbox__name">{g.fullName}</span>
              <span className="composer-listbox__meta">
                {g.phone ?? "no phone"}
                {g.email !== null ? ` · ${g.email}` : ""}
                {g.dob !== null ? ` · DOB ${prettyDate(g.dob)}` : ""}
                {g.stayCount > 0 ? ` · ${g.stayCount} stay${g.stayCount === 1 ? "" : "s"}` : ""}
              </span>
            </li>
          ))}
          <li className="composer-listbox__addnew">
            <button
              type="button"
              className="console-btn console-btn--ghost console-btn--sm"
              onMouseDown={(e) => {
                e.preventDefault();
                onAddNew(q);
                setOpen(false);
              }}
            >
              + Add new guest
            </button>
          </li>
        </ul>
      ) : null}
    </div>
  );
}

// ===========================================================================
// New-guest drawer (with dedup)
// ===========================================================================

function NewGuestDrawer({
  initialQuery,
  onClose,
  onCreated,
}: {
  readonly initialQuery: string;
  readonly onClose: () => void;
  readonly onCreated: (g: GuestSearchRow) => void;
}): ReactElement {
  const looksPhone = /\d{4,}/.test(initialQuery);
  const [firstName, setFirstName] = useState(looksPhone ? "" : initialQuery);
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState(looksPhone ? initialQuery : "");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [idType, setIdType] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [gstin, setGstin] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<UserMessageModel | null>(null);
  const [dupes, setDupes] = useState<readonly GuestSearchRow[]>([]);
  const [dupeChecked, setDupeChecked] = useState(false);

  const runDedup = useCallback(async (): Promise<boolean> => {
    if (phone.trim() === "" && email.trim() === "") return false;
    const matches = await apiCheckDuplicateGuest({
      ...(phone.trim() !== "" ? { phone: phone.trim() } : {}),
      ...(email.trim() !== "" ? { email: email.trim() } : {}),
    });
    setDupes(matches);
    setDupeChecked(true);
    return matches.length > 0;
  }, [phone, email]);

  async function create(): Promise<void> {
    if (firstName.trim() === "") {
      setErr({ what: "First name is required.", why: "A guest needs at least a name.", next: "Enter the guest's name." });
      return;
    }
    if (phone.trim() === "") {
      setErr({ what: "Phone is required.", why: "Phone is the strongest unique guest identifier.", next: "Enter a phone number." });
      return;
    }
    setBusy(true);
    setErr(null);
    const fullName = `${firstName.trim()}${lastName.trim() !== "" ? ` ${lastName.trim()}` : ""}`;
    const result = await apiCreateGuestFull({
      fullName,
      phone: phone.trim(),
      ...(email.trim() !== "" ? { email: email.trim() } : {}),
      ...(dob !== "" ? { dob } : {}),
      ...(city.trim() !== "" ? { city: city.trim() } : {}),
      ...(country.trim() !== "" ? { country: country.trim() } : {}),
      ...(idType.trim() !== "" ? { idType: idType.trim() } : {}),
      ...(idNumber.trim() !== "" ? { idNumber: idNumber.trim() } : {}),
      ...(gstin.trim() !== "" ? { gstin: gstin.trim() } : {}),
      ...(notes.trim() !== "" ? { notes: notes.trim() } : {}),
    });
    setBusy(false);
    const e = asError(result);
    if (e !== null) {
      setErr(toUserMessage(e));
      return;
    }
    const id = (result as { readonly id: string }).id;
    onCreated({
      id,
      fullName,
      email: email.trim() !== "" ? email.trim() : null,
      phone: phone.trim(),
      dob: dob !== "" ? dob : null,
      city: city.trim() !== "" ? city.trim() : null,
      country: country.trim() !== "" ? country.trim() : null,
      tags: [],
      preferences: null,
      dietary_notes: null,
      stayCount: 0,
    });
  }

  return (
    <div className="composer-subdrawer" role="dialog" aria-label="Add new guest">
      <div className="composer-subdrawer__head">
        <h3>Add new guest</h3>
        <button type="button" className="console-btn console-btn--ghost console-btn--sm" onClick={onClose}>
          Close
        </button>
      </div>
      {err !== null ? <UserMessage message={err} severity="error" /> : null}
      {dupes.length > 0 ? (
        <div className="composer-dupe">
          <p className="composer-dupe__title">Possible existing guest found</p>
          {dupes.map((d) => (
            <div key={d.id} className="composer-dupe__row">
              <span>
                {d.fullName} · {d.phone ?? "no phone"}
                {d.stayCount > 0 ? ` · ${d.stayCount} previous stay${d.stayCount === 1 ? "" : "s"}` : ""}
              </span>
              <button type="button" className="console-btn console-btn--sm" onClick={() => onCreated(d)}>
                Use existing guest
              </button>
            </div>
          ))}
          <button type="button" className="console-btn console-btn--ghost console-btn--sm" onClick={() => void create()}>
            Create new anyway
          </button>
        </div>
      ) : null}
      <div className="console-form__row">
        <div className="console-field">
          <label htmlFor="ng-first">First name *</label>
          <input id="ng-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div className="console-field">
          <label htmlFor="ng-last">Last name</label>
          <input id="ng-last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
      </div>
      <div className="console-form__row">
        <div className="console-field">
          <label htmlFor="ng-phone">Phone *</label>
          <input id="ng-phone" value={phone} onChange={(e) => setPhone(e.target.value)} onBlur={() => void runDedup()} />
        </div>
        <div className="console-field">
          <label htmlFor="ng-email">Email</label>
          <input id="ng-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} onBlur={() => void runDedup()} />
        </div>
      </div>
      <div className="console-form__row">
        <div className="console-field">
          <label htmlFor="ng-dob">Date of birth</label>
          <input id="ng-dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
        </div>
        <div className="console-field">
          <label htmlFor="ng-city">City</label>
          <input id="ng-city" value={city} onChange={(e) => setCity(e.target.value)} />
        </div>
        <div className="console-field">
          <label htmlFor="ng-country">Country</label>
          <input id="ng-country" value={country} onChange={(e) => setCountry(e.target.value)} />
        </div>
      </div>
      <div className="console-form__row">
        <div className="console-field">
          <label htmlFor="ng-idtype">ID type</label>
          <input id="ng-idtype" value={idType} onChange={(e) => setIdType(e.target.value)} placeholder="Passport / Aadhaar / …" />
        </div>
        <div className="console-field">
          <label htmlFor="ng-idnum">ID number</label>
          <input id="ng-idnum" value={idNumber} onChange={(e) => setIdNumber(e.target.value)} />
        </div>
        <div className="console-field">
          <label htmlFor="ng-gstin">GSTIN / Tax number</label>
          <input id="ng-gstin" value={gstin} onChange={(e) => setGstin(e.target.value)} />
        </div>
      </div>
      <div className="console-field">
        <label htmlFor="ng-notes">Notes</label>
        <textarea id="ng-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <div className="composer-subdrawer__actions">
        <button
          type="button"
          className="console-btn"
          disabled={busy}
          onClick={() => {
            void (async () => {
              if (!dupeChecked) {
                const has = await runDedup();
                if (has) return; // show dupes; user decides
              }
              await create();
            })();
          }}
        >
          {busy ? "Adding…" : "Add guest"}
        </button>
      </div>
    </div>
  );
}

// ===========================================================================
// The composer (large modal)
// ===========================================================================

export function BookingComposer({
  properties,
  onClose,
  onBooked,
}: {
  readonly properties: readonly Property[];
  readonly onClose: () => void;
  readonly onBooked: () => void;
}): ReactElement {
  // Stay
  const [propertyId, setPropertyId] = useState("");
  const [propertyQuery, setPropertyQuery] = useState("");
  const [units, setUnits] = useState<readonly Unit[]>([]);
  const [unitId, setUnitId] = useState("");
  const [ratePlanId, setRatePlanId] = useState("");
  const [checkIn, setCheckIn] = useState(isoDateOffset(1));
  const [checkOut, setCheckOut] = useState(isoDateOffset(3));
  const [adults, setAdults] = useState("2");
  const [children, setChildren] = useState("0");
  const [infants, setInfants] = useState("0");

  // Guest
  const [guest, setGuest] = useState<GuestSearchRow | null>(null);
  const [addingGuest, setAddingGuest] = useState(false);
  const [addQuery, setAddQuery] = useState("");

  // Pricing
  const [overrideOn, setOverrideOn] = useState(false);
  const [overrideRate, setOverrideRate] = useState("");
  const [overrideReason, setOverrideReason] = useState<string>(RATE_OVERRIDE_REASONS[0]);
  const [discountType, setDiscountType] = useState<"none" | "percentage" | "fixed">("none");
  const [discountValue, setDiscountValue] = useState("");
  const [addons, setAddons] = useState<readonly AddonRow[]>([]);

  // Payment
  const [payments, setPayments] = useState<readonly PaymentRow[]>([]);

  // Booking details
  const [staff, setStaff] = useState<readonly StaffOption[]>([]);
  const [bookedById, setBookedById] = useState("");
  const [source, setSource] = useState("Direct");
  const [arrivalTime, setArrivalTime] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");
  const [internalNotes, setInternalNotes] = useState("");

  // Quote + outcome
  const [quote, setQuote] = useState<QuoteResult | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<UserMessageModel | null>(null);
  // Monotonic request version: only the LATEST quote response may update state,
  // so an older (possibly failed) response can never overwrite a newer success
  // and leave a stale error banner behind (async-race protection).
  const quoteSeqRef = useRef(0);
  const [done, setDone] = useState<{ readonly code: string | null; readonly ref: string; readonly total: string; readonly currency: string; readonly status: string } | null>(null);

  const filteredProperties = useMemo(() => {
    const q = propertyQuery.trim().toLowerCase();
    if (q === "") return properties;
    return properties.filter((p) => p.name.toLowerCase().includes(q));
  }, [properties, propertyQuery]);

  const selectedUnit = units.find((u) => u.id === unitId) ?? null;
  const currency = quote?.currency ?? selectedUnit?.baseRateCurrency ?? "INR";

  // Lock the page scroll while the composer is open (restored on close).
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Load units on property change.
  useEffect(() => {
    if (propertyId === "") {
      setUnits([]);
      setUnitId("");
      return;
    }
    let live = true;
    apiListUnits(propertyId)
      .then((u) => {
        if (live) setUnits(u);
      })
      .catch(() => {
        if (live) setUnits([]);
      });
    return () => {
      live = false;
    };
  }, [propertyId]);

  // Load staff once.
  useEffect(() => {
    let live = true;
    apiListStaff()
      .then((s) => {
        if (live) setStaff(s);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  // Live quote whenever pricing inputs change.
  const addonsKey = addons.map((a) => `${a.type}:${a.qty}:${a.priceMinor}`).join("|");
  useEffect(() => {
    // Any prior error is stale the moment the stay inputs change — clear it and
    // only the LATEST request's response may set a new one (version-guarded).
    setErr(null);
    const seq = ++quoteSeqRef.current;
    const isCurrent = () => seq === quoteSeqRef.current;
    if (unitId === "" || checkIn === "" || checkOut === "" || checkOut <= checkIn) {
      setQuote(null);
      setQuoting(false);
      return;
    }
    setQuoting(true);
    const overrideMinor = overrideOn ? parseMoneyToMinor(overrideRate) : null;
    apiQuote({
      unitId,
      checkIn,
      checkOut,
      ...(ratePlanId !== "" ? { ratePlanId } : {}),
      ...(overrideMinor !== null ? { overrideRateMinor: overrideMinor } : {}),
      addons: addons.map((a) => ({ type: a.type, name: a.name, qty: a.qty, priceMinor: a.priceMinor })),
      discountType,
      ...(discountValue !== "" ? { discountValue: Number(discountValue) } : {}),
    })
      .then((res) => {
        // Drop any response that is not from the latest request (async race).
        if (!isCurrent()) return;
        setQuoting(false);
        const qe = asError(res);
        if (qe !== null) {
          setQuote(null);
          // Surface the real reason the price/availability could not be resolved.
          setErr(toUserMessage(qe));
          return;
        }
        // A successful quote MUST clear any residual error (valid state → no banner).
        setQuote(res as QuoteResult);
        setErr(null);
      })
      .catch(() => {
        if (!isCurrent()) return;
        setQuoting(false);
        setQuote(null);
        setErr({
          what: "Unable to calculate the booking price.",
          why: "The pricing request could not be completed.",
          next: "Check your connection and try again.",
        });
      });
  }, [unitId, ratePlanId, checkIn, checkOut, overrideOn, overrideRate, discountType, discountValue, addonsKey]);

  // Clear a stale BLOCKING error whenever any OTHER booking input the quote
  // effect does not watch changes (guest, occupancy, payments). A prior failed
  // Confirm (e.g. "No guest selected") must not linger once the user fixes it.
  const paymentsKey = payments.map((p) => `${p.method}:${p.amountMinor}`).join("|");
  useEffect(() => {
    setErr(null);
  }, [guest, adults, children, infants, paymentsKey, propertyId]);

  const totalMinor = quote !== null ? Number(quote.totalMinor) : 0;
  const paidMinor = payments.reduce((s, p) => s + p.amountMinor, 0);
  const balanceMinor = Math.max(0, totalMinor - paidMinor);
  const paymentStatus = paidMinor <= 0 ? "Unpaid" : paidMinor >= totalMinor && totalMinor > 0 ? "Paid" : "Partially Paid";
  const overpaid = totalMinor > 0 && paidMinor > totalMinor;

  const datesValid = checkOut > checkIn;
  const nights = quote?.nights ?? 0;

  // STRUCTURAL INVARIANT (fixes the stale banner): a blocking error must NEVER
  // be shown while the booking is in a valid, available, confirmable state. If
  // the latest quote resolved, the room is available, dates are valid, nothing
  // is overpaid, and we are not mid-request, there is no blocking condition —
  // suppress any residual error regardless of how it was set. A genuine problem
  // (quote failed → quote===null, unavailable, overpaid, invalid dates) still
  // surfaces the banner.
  const bookingLooksValid =
    quote !== null && quote.available !== false && datesValid && !overpaid && !quoting;
  const showBlockingError = err !== null && !bookingLooksValid;

  function addAddon(preset: { type: string; name: string }): void {
    setAddons((rows) => [...rows, { key: nextKey(), type: preset.type, name: preset.name, qty: 1, priceMinor: 0 }]);
  }
  function addPayment(): void {
    setPayments((rows) => [
      ...rows,
      { key: nextKey(), method: "Cash", amountMinor: balanceMinor > 0 ? balanceMinor : 0, reference: "" },
    ]);
  }

  async function submit(action: "hold" | "confirm"): Promise<void> {
    setErr(null);
    if (unitId === "") {
      setErr({ what: "No unit selected.", why: "A booking needs a unit.", next: "Pick a property and unit." });
      return;
    }
    if (guest === null || guest.id === "") {
      setErr({ what: "No guest selected.", why: "A booking needs a guest.", next: "Search or add a guest." });
      return;
    }
    if (!datesValid) {
      setErr({ what: "Invalid dates.", why: "Check-out must be after check-in.", next: "Adjust the dates." });
      return;
    }
    if (overpaid) {
      setErr({ what: "Payments exceed the total.", why: "The sum of payments cannot exceed the booking total.", next: "Reduce a payment amount." });
      return;
    }
    const src = BOOKING_SOURCES.find((s) => s.label === source) ?? BOOKING_SOURCES[0]!;
    setBusy(true);
    const result = await apiCompose({
      action,
      unitId,
      guestId: guest.id,
      checkIn,
      checkOut,
      adults: Number(adults) || 1,
      children: Number(children) || 0,
      infants: Number(infants) || 0,
      nightlyRateMinor: quote !== null ? Number(quote.nightlyRateMinor) : 0,
      ...(quote?.ratePlan ? { ratePlanName: quote.ratePlan.name } : {}),
      sourceType: src.sourceType,
      source: src.label,
      ...(bookedById !== "" ? { bookedById } : {}),
      paymentMode: "pay_at_property",
      addons: addons.map((a) => ({ type: a.type, name: a.name, qty: a.qty, priceMinor: a.priceMinor })),
      discountType,
      ...(discountValue !== "" ? { discountValue: Number(discountValue) } : {}),
      rateOverridden: overrideOn,
      ...(overrideOn ? { rateOverrideReason: overrideReason } : {}),
      ...(arrivalTime !== "" ? { arrivalTime } : {}),
      ...(specialRequests.trim() !== "" ? { specialRequests: specialRequests.trim() } : {}),
      ...(internalNotes.trim() !== "" ? { internalNotes: internalNotes.trim() } : {}),
      payments: payments
        .filter((p) => p.amountMinor > 0)
        .map((p) => ({ method: p.method, amountMinor: p.amountMinor, ...(p.reference ? { reference: p.reference } : {}) })),
    });
    setBusy(false);
    const e = asError(result);
    if (e !== null) {
      setErr(toUserMessage(e));
      return;
    }
    const r = result as ComposeResult;
    setDone({
      code: r.confirmationCode,
      ref: r.confirmationCode ?? r.reservationId.slice(0, 8),
      total: quote?.totalMinor ?? "0",
      currency: r.currency,
      status: r.status,
    });
    onBooked();
  }

  // Success screen
  if (done !== null) {
    return (
      <div className="composer-modal" role="dialog" aria-modal="true" aria-label="Booking confirmed">
        <div className="composer-modal__panel composer-modal__panel--sm">
          <div className="composer-success">
            <div className="composer-success__badge" aria-hidden="true">✓</div>
            <h2>{done.status === "confirmed" ? "Booking confirmed" : "Hold placed"}</h2>
            <p className="composer-success__ref">Reference: <strong>{done.ref}</strong></p>
            <p>Guest: {guest?.fullName}</p>
            <p>Stay: {prettyDate(checkIn)} → {prettyDate(checkOut)}</p>
            <p>Total: {formatMoney(Number(done.total), done.currency)}</p>
            <div className="composer-success__actions">
              <button type="button" className="console-btn" onClick={onClose}>Done</button>
              <button
                type="button"
                className="console-btn console-btn--ghost"
                onClick={() => {
                  setDone(null);
                  setGuest(null);
                  setUnitId("");
                  setAddons([]);
                  setPayments([]);
                  setDiscountType("none");
                  setDiscountValue("");
                  setOverrideOn(false);
                  setErr(null);
                }}
              >
                New booking
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="composer-modal" role="dialog" aria-modal="true" aria-label="New booking">
      <div className="composer-modal__panel">
        <header className="composer-modal__head">
          <h2>New Booking</h2>
          <button type="button" className="console-btn console-btn--ghost console-btn--sm" onClick={onClose}>Close</button>
        </header>

        <div className="composer-body">
          {/* LEFT: inputs */}
          <div className="composer-left">
            {showBlockingError && err !== null ? <UserMessage message={err} severity="error" /> : null}

            {/* Stay */}
            <section className="composer-section">
              <h3 className="composer-section__title">Stay</h3>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="cm-prop">Property *</label>
                  {properties.length > 6 ? (
                    <input
                      id="cm-prop-search"
                      placeholder="Search properties…"
                      value={propertyQuery}
                      onChange={(e) => setPropertyQuery(e.target.value)}
                    />
                  ) : null}
                  <select id="cm-prop" value={propertyId} onChange={(e) => { setPropertyId(e.target.value); setUnitId(""); setRatePlanId(""); }}>
                    <option value="">Select a property…</option>
                    {filteredProperties.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="console-field">
                  <label htmlFor="cm-unit">Unit / Room type *</label>
                  <select id="cm-unit" value={unitId} disabled={propertyId === ""} onChange={(e) => { setUnitId(e.target.value); setRatePlanId(""); }}>
                    <option value="">Select a unit…</option>
                    {units.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} · {formatMoney(u.baseRateMinor, u.baseRateCurrency)}/night · {u.maxOccupancy} guests
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="cm-in">Check-in *</label>
                  <input id="cm-in" type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} />
                </div>
                <div className="console-field">
                  <label htmlFor="cm-out">Check-out *</label>
                  <input id="cm-out" type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} aria-invalid={!datesValid} />
                </div>
                <div className="console-field">
                  <span className="console-field__label-static">Nights</span>
                  <p className="composer-readonly">{nights > 0 ? nights : "—"}</p>
                </div>
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="cm-adults">Adults *</label>
                  <input id="cm-adults" type="number" min="1" value={adults} onChange={(e) => setAdults(e.target.value)} />
                </div>
                <div className="console-field">
                  <label htmlFor="cm-children">Children</label>
                  <input id="cm-children" type="number" min="0" value={children} onChange={(e) => setChildren(e.target.value)} />
                </div>
                <div className="console-field">
                  <label htmlFor="cm-infants">Infants</label>
                  <input id="cm-infants" type="number" min="0" value={infants} onChange={(e) => setInfants(e.target.value)} />
                </div>
              </div>
              {quote !== null ? (
                <p className={`composer-avail composer-avail--${quote.available === false ? "no" : "yes"}`}>
                  {quote.available === false
                    ? "Unavailable for selected dates"
                    : quote.available === true
                      ? "Available for selected dates"
                      : "Availability unknown"}
                </p>
              ) : null}
            </section>

            {/* Guest */}
            <section className="composer-section">
              <h3 className="composer-section__title">Guest</h3>
              <GuestCombobox
                selected={guest !== null && guest.id !== "" ? guest : null}
                onSelect={(g) => setGuest(g.id === "" ? null : g)}
                onAddNew={(q) => { setAddQuery(q); setAddingGuest(true); }}
              />
              {guest !== null && guest.id !== "" && (guest.stayCount > 0 || guest.preferences !== null) ? (
                <p className="composer-guest-hint">
                  {guest.stayCount > 0 ? `Returning guest · ${guest.stayCount} stay${guest.stayCount === 1 ? "" : "s"}` : "New guest"}
                  {guest.preferences !== null ? ` · Preferences: ${guest.preferences}` : ""}
                  {guest.dietary_notes !== null ? ` · ${guest.dietary_notes}` : ""}
                </p>
              ) : null}
              {addingGuest ? (
                <NewGuestDrawer
                  initialQuery={addQuery}
                  onClose={() => setAddingGuest(false)}
                  onCreated={(g) => { setGuest(g); setAddingGuest(false); }}
                />
              ) : null}
            </section>

            {/* Pricing */}
            <section className="composer-section">
              <h3 className="composer-section__title">Pricing</h3>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="cm-rateplan">Rate plan</label>
                  <select id="cm-rateplan" value={ratePlanId} onChange={(e) => setRatePlanId(e.target.value)} disabled={unitId === ""}>
                    <option value="">Default</option>
                    {(quote?.ratePlans ?? []).map((rp) => (
                      <option key={rp.id} value={rp.id}>{rp.name} · base {formatMoney(Number(rp.rateMinor), currency)}</option>
                    ))}
                  </select>
                </div>
                <div className="console-field">
                  <span className="console-field__label-static">Nightly rate</span>
                  {quote === null ? (
                    <p className="composer-readonly">—</p>
                  ) : (
                    <div className="composer-rate">
                      {/* Selected-date (resolved) rate — the price actually used. */}
                      <p className="composer-readonly">
                        {quote.ratesVary
                          ? `From ${formatMoney(Number(quote.nightlyRateMinor), currency)} / night`
                          : `${formatMoney(Number(quote.nightlyRateMinor), currency)} / night`}
                      </p>
                      {/* Explain WHY it differs from the plan base, when it does. */}
                      {quote.baseRateMinor !== undefined && Number(quote.baseRateMinor) !== Number(quote.nightlyRateMinor) ? (
                        <p className="composer-rate__note">
                          Base rate {formatMoney(Number(quote.baseRateMinor), currency)} · selected-date rate {formatMoney(Number(quote.nightlyRateMinor), currency)}
                          {quote.dynamicPricing ? " (pricing rules applied)" : ""}
                        </p>
                      ) : null}
                      {Array.isArray(quote.nightlyBreakdown) && quote.nightlyBreakdown.length > 0 ? (
                        <details className="composer-rate__breakdown">
                          <summary>View nightly breakdown</summary>
                          <table className="composer-breakdown-table">
                            <thead><tr><th>Night</th><th>Base</th><th>Daily override</th><th>Final</th></tr></thead>
                            <tbody>
                              {quote.nightlyBreakdown.map((n) => (
                                <tr key={n.day}>
                                  <td>{prettyDate(n.day)}</td>
                                  <td>{formatMoney(Number(n.baseRateMinor), currency)}</td>
                                  <td>{n.dailyOverrideMinor !== null ? formatMoney(Number(n.dailyOverrideMinor), currency) : "—"}</td>
                                  <td><strong>{formatMoney(Number(n.finalRateMinor), currency)}</strong></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </details>
                      ) : null}
                    </div>
                  )}
                </div>
                <div className="console-field console-field--checkbox">
                  <label>
                    <input type="checkbox" checked={overrideOn} onChange={(e) => setOverrideOn(e.target.checked)} />
                    Override rate
                  </label>
                </div>
              </div>
              {overrideOn ? (
                <div className="console-form__row">
                  <div className="console-field">
                    <label htmlFor="cm-override">New nightly rate</label>
                    <input id="cm-override" inputMode="decimal" placeholder="12000.00" value={overrideRate} onChange={(e) => setOverrideRate(e.target.value)} />
                  </div>
                  <div className="console-field">
                    <label htmlFor="cm-reason">Override reason</label>
                    <select id="cm-reason" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}>
                      {RATE_OVERRIDE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
              ) : null}

              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="cm-disctype">Discount</label>
                  <select id="cm-disctype" value={discountType} onChange={(e) => setDiscountType(e.target.value as "none" | "percentage" | "fixed")}>
                    <option value="none">None</option>
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed amount</option>
                  </select>
                </div>
                {discountType !== "none" ? (
                  <div className="console-field">
                    <label htmlFor="cm-discval">{discountType === "percentage" ? "Percent" : "Amount"}</label>
                    <input id="cm-discval" inputMode="decimal" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} placeholder={discountType === "percentage" ? "10" : "2000"} />
                  </div>
                ) : null}
              </div>

              <div className="composer-addons">
                <div className="composer-addons__head">
                  <span>Add-ons / extra charges</span>
                </div>
                {addons.map((a, i) => (
                  <div key={a.key} className="composer-addons__row">
                    <input
                      aria-label="Add-on name"
                      value={a.name}
                      onChange={(e) => setAddons((rows) => rows.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                    />
                    <input
                      aria-label="Quantity"
                      type="number"
                      min="1"
                      value={a.qty}
                      onChange={(e) => setAddons((rows) => rows.map((x, j) => j === i ? { ...x, qty: Math.max(1, Number(e.target.value) || 1) } : x))}
                    />
                    <input
                      aria-label="Price"
                      inputMode="decimal"
                      placeholder="0.00"
                      onChange={(e) => {
                        const minor = parseMoneyToMinor(e.target.value) ?? 0;
                        setAddons((rows) => rows.map((x, j) => j === i ? { ...x, priceMinor: minor } : x));
                      }}
                    />
                    <button type="button" className="console-btn console-btn--ghost console-btn--sm" onClick={() => setAddons((rows) => rows.filter((_, j) => j !== i))}>Remove</button>
                  </div>
                ))}
                <div className="composer-addons__presets">
                  {ADDON_PRESETS.map((p) => (
                    <button key={p.type} type="button" className="console-btn console-btn--ghost console-btn--sm" onClick={() => addAddon(p)}>+ {p.name}</button>
                  ))}
                </div>
              </div>
              <p className="composer-taxnote">Tax mode: auto from Accounts/Invoice settings (GST computed by the shared engine).</p>
            </section>

            {/* Payment */}
            <section className="composer-section">
              <h3 className="composer-section__title">Payment</h3>
              <p className="composer-paystatus">
                Status: <strong>{paymentStatus}</strong>
                {overpaid ? <span className="composer-avail composer-avail--no"> · payments exceed total</span> : null}
              </p>
              {payments.map((p, i) => (
                <div key={p.key} className="composer-pay__row">
                  <select
                    aria-label="Payment method"
                    value={p.method}
                    onChange={(e) => setPayments((rows) => rows.map((x, j) => j === i ? { ...x, method: e.target.value } : x))}
                  >
                    {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <input
                    aria-label="Amount"
                    inputMode="decimal"
                    placeholder="0.00"
                    defaultValue={p.amountMinor > 0 ? (p.amountMinor / 100).toFixed(2) : ""}
                    onChange={(e) => {
                      const minor = parseMoneyToMinor(e.target.value) ?? 0;
                      setPayments((rows) => rows.map((x, j) => j === i ? { ...x, amountMinor: minor } : x));
                    }}
                  />
                  <input
                    aria-label="Reference"
                    placeholder="Ref / UTR"
                    value={p.reference ?? ""}
                    onChange={(e) => setPayments((rows) => rows.map((x, j) => j === i ? { ...x, reference: e.target.value } : x))}
                  />
                  <button type="button" className="console-btn console-btn--ghost console-btn--sm" onClick={() => setPayments((rows) => rows.filter((_, j) => j !== i))}>Remove</button>
                </div>
              ))}
              <button type="button" className="console-btn console-btn--ghost console-btn--sm" onClick={addPayment}>+ Add payment</button>
              <p className="composer-paylink composer-paylink--disabled" title="No payment gateway configured">Send payment link · unavailable</p>
            </section>

            {/* Booking details */}
            <section className="composer-section">
              <h3 className="composer-section__title">Booking details</h3>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="cm-source">Booking source</label>
                  <select id="cm-source" value={source} onChange={(e) => setSource(e.target.value)}>
                    {BOOKING_SOURCES.map((s) => <option key={s.label} value={s.label}>{s.label}</option>)}
                  </select>
                </div>
                <div className="console-field">
                  <label htmlFor="cm-bookedby">Booked by</label>
                  <select id="cm-bookedby" value={bookedById} onChange={(e) => setBookedById(e.target.value)}>
                    <option value="">Unassigned</option>
                    {staff.map((s) => <option key={s.id} value={s.id}>{s.name}{s.role ? ` · ${s.role}` : ""}</option>)}
                  </select>
                </div>
                <div className="console-field">
                  <label htmlFor="cm-arrival">Arrival time</label>
                  <input id="cm-arrival" type="time" value={arrivalTime} onChange={(e) => setArrivalTime(e.target.value)} />
                </div>
              </div>
              <div className="console-field">
                <label htmlFor="cm-special">Special requests (guest-visible)</label>
                <textarea id="cm-special" rows={2} value={specialRequests} onChange={(e) => setSpecialRequests(e.target.value)} placeholder="Vegetarian meals, airport pickup, late arrival…" />
              </div>
              <div className="console-field">
                <label htmlFor="cm-internal">Internal notes (staff only)</label>
                <textarea id="cm-internal" rows={2} value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} />
              </div>
            </section>
          </div>

          {/* RIGHT: sticky summary */}
          <aside className="composer-summary" aria-label="Booking summary">
            <h3 className="composer-summary__title">Booking Summary</h3>
            {quote === null ? (
              <p className="composer-summary__empty">Select a property, unit and dates to build the booking summary.</p>
            ) : (
              <>
                <dl className="composer-summary__meta">
                  <div><dt>Property</dt><dd>{properties.find((p) => p.id === propertyId)?.name ?? "—"}</dd></div>
                  <div><dt>Unit</dt><dd>{selectedUnit?.name ?? "—"}</dd></div>
                  {quote.ratePlan ? <div><dt>Rate plan</dt><dd>{quote.ratePlan.name}</dd></div> : null}
                  <div><dt>Guest</dt><dd>{guest?.fullName ?? "—"}</dd></div>
                  <div><dt>Stay</dt><dd>{prettyDate(checkIn)} → {prettyDate(checkOut)} · {nights} night{nights === 1 ? "" : "s"}</dd></div>
                  <div><dt>Guests</dt><dd>{adults} adult{Number(adults) === 1 ? "" : "s"}{Number(children) > 0 ? `, ${children} children` : ""}</dd></div>
                  <div><dt>Source</dt><dd>{source}</dd></div>
                  {bookedById !== "" ? <div><dt>Booked by</dt><dd>{staff.find((s) => s.id === bookedById)?.name ?? "—"}</dd></div> : null}
                </dl>
                <dl className="composer-summary__price">
                  <div><dt>Room charges</dt><dd>{formatMoney(Number(quote.roomsSubtotalMinor), currency)}</dd></div>
                  {Number(quote.extrasMinor) > 0 ? <div><dt>Extras</dt><dd>{formatMoney(Number(quote.extrasMinor), currency)}</dd></div> : null}
                  <div><dt>Subtotal</dt><dd>{formatMoney(Number(quote.subtotalMinor), currency)}</dd></div>
                  {Number(quote.discountMinor) > 0 ? <div><dt>Discount</dt><dd>− {formatMoney(Number(quote.discountMinor), currency)}</dd></div> : null}
                  <div><dt>Taxable value</dt><dd>{formatMoney(Number(quote.taxableMinor), currency)}</dd></div>
                  <div><dt>GST / Tax</dt><dd>{formatMoney(Number(quote.taxMinor), currency)}</dd></div>
                  <div className="composer-summary__total"><dt>Total</dt><dd>{formatMoney(Number(quote.totalMinor), currency)}</dd></div>
                  <div><dt>Paid</dt><dd>{formatMoney(paidMinor, currency)}</dd></div>
                  <div className="composer-summary__balance"><dt>Balance due</dt><dd>{formatMoney(balanceMinor, currency)}</dd></div>
                </dl>
                <p className={`composer-avail composer-avail--${quote.available === false ? "no" : "yes"}`}>
                  {quote.available === false ? "Conflict detected" : "Available"}
                </p>
              </>
            )}
            <div className="composer-summary__actions">
              <button type="button" className="console-btn console-btn--ghost" disabled={busy || quoting} onClick={() => void submit("hold")}>
                {busy ? "Working…" : "Place Hold"}
              </button>
              <button type="button" className="console-btn" disabled={busy || quoting || quote === null || quote.available === false} onClick={() => void submit("confirm")}>
                {busy ? "Working…" : "Confirm Booking"}
              </button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Reservations table
// ===========================================================================

function ReservationsPanel({
  reservations,
  onConfirm,
  onCancel,
  actingId,
}: {
  readonly reservations: readonly Reservation[];
  readonly onConfirm: (id: string) => void;
  readonly onCancel: (id: string) => void;
  readonly actingId: string | null;
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Reservations">
      <h2 className="console-panel__title">Reservations</h2>
      {reservations.length > 0 ? (
        <table className="console-table">
          <caption className="console-sr-only">All reservations</caption>
          <thead>
            <tr>
              <th scope="col">Reference</th>
              <th scope="col">Dates</th>
              <th scope="col">Status</th>
              <th scope="col">Amount</th>
              <th scope="col">Payment</th>
              <th scope="col">Source</th>
              <th scope="col">Hold expiry</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {reservations.map((r) => {
              const isActing = actingId === r.id;
              const canConfirm = r.status === "held";
              const canCancel = r.status === "held" || r.status === "confirmed";
              return (
                <tr key={r.id}>
                  <td>{r.confirmationCode ?? r.id.slice(0, 8)}</td>
                  <td>{r.checkIn} → {r.checkOut}</td>
                  <td><span className={statusBadgeClass(r.status)}>{r.status}</span></td>
                  <td className="booking-num">{formatMoney(Number(r.amountMinor), r.currency)}</td>
                  <td>{r.paymentMode}</td>
                  <td>{r.channel}</td>
                  <td>{r.status === "held" && r.holdExpiresAt !== null ? formatDateTime(r.holdExpiresAt) : "—"}</td>
                  <td>
                    <div className="booking-rowactions">
                      {canConfirm ? (
                        <button type="button" className="console-btn console-btn--ghost" disabled={isActing} onClick={() => onConfirm(r.id)}>
                          {isActing ? "Working…" : "Confirm"}
                        </button>
                      ) : null}
                      {canCancel ? (
                        <button type="button" className="console-btn console-btn--ghost" disabled={isActing} onClick={() => onCancel(r.id)}>
                          {isActing ? "Working…" : "Cancel"}
                        </button>
                      ) : null}
                      {!canConfirm && !canCancel ? "—" : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className="console-empty">No reservations yet. Start a new booking above.</p>
      )}
    </section>
  );
}

// ===========================================================================
// Top-level screen
// ===========================================================================

interface BookingData {
  readonly properties: readonly Property[];
  readonly reservations: readonly Reservation[];
}
type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly data: BookingData }
  | { readonly kind: "error" };

async function loadAll(): Promise<BookingData> {
  const [properties, reservations] = await Promise.all([apiListProperties(), apiListReservations()]);
  return { properties, reservations };
}

export default function BookingScreen(): ReactElement {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [composerOpen, setComposerOpen] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);
  const [expiring, setExpiring] = useState(false);
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  const [status, setStatus] = useState<UserMessageModel | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const data = await loadAll();
      setState({ kind: "ready", data });
    } catch {
      setState({ kind: "error" });
    }
  }, []);

  useEffect(() => {
    let active = true;
    loadAll()
      .then((data) => { if (active) setState({ kind: "ready", data }); })
      .catch(() => { if (active) setState({ kind: "error" }); });
    return () => { active = false; };
  }, []);

  const handleConfirm = useCallback(async (id: string): Promise<void> => {
    setApiError(null); setStatus(null); setActingId(id);
    try {
      const result = await apiConfirmBooking({ reservationId: id });
      const e = asError(result);
      if (e !== null) { setApiError(toUserMessage(e)); return; }
      setStatus({ what: "Booking confirmed.", why: "The reservation is now confirmed.", next: "Availability was synced to Channex automatically." });
      await refresh();
    } catch {
      setApiError({ what: "Could not confirm.", why: "The booking service request failed.", next: "Try again." });
    } finally { setActingId(null); }
  }, [refresh]);

  const handleCancel = useCallback(async (id: string): Promise<void> => {
    setApiError(null); setStatus(null); setActingId(id);
    try {
      const result = await apiCancelBooking({ reservationId: id });
      const e = asError(result);
      if (e !== null) { setApiError(toUserMessage(e)); return; }
      setStatus({ what: "Reservation cancelled.", why: "The unit is available again.", next: "Channex availability was restored automatically." });
      await refresh();
    } catch {
      setApiError({ what: "Could not cancel.", why: "The booking service request failed.", next: "Try again." });
    } finally { setActingId(null); }
  }, [refresh]);

  const handleExpireHolds = useCallback(async (): Promise<void> => {
    setApiError(null); setStatus(null); setExpiring(true);
    try {
      const result = await apiExpireHolds();
      const e = asError(result);
      if (e !== null) { setApiError(toUserMessage(e)); return; }
      const swept = result as ExpireHoldsResult;
      setStatus({ what: "Expired holds swept.", why: `${swept.expired} hold${swept.expired === 1 ? "" : "s"} freed inventory.`, next: swept.expired > 0 ? "Freed units are bookable again." : "No holds had expired." });
      await refresh();
    } catch {
      setApiError({ what: "Could not sweep holds.", why: "The booking service request failed.", next: "Try again." });
    } finally { setExpiring(false); }
  }, [refresh]);

  return (
    <section aria-labelledby="booking-heading" data-console-screen="booking">
      <p className="console-page-eyebrow">Direct · Bookings</p>
      <div className="console-page-head">
        <div>
          <h1 id="booking-heading">Direct Booking</h1>
          <p className="console-lede">
            An assisted reservation workflow — searchable guests, automatic rate & GST, split payments, and
            automatic Channex inventory sync. Two stays can never overlap on the same unit.
          </p>
        </div>
        {state.kind === "ready" ? (
          <button type="button" className="console-btn" onClick={() => setComposerOpen(true)}>+ New booking</button>
        ) : null}
      </div>

      {apiError !== null ? <UserMessage message={apiError} severity="error" /> : null}
      {status !== null ? <UserMessage message={status} severity="status" /> : null}

      {state.kind === "loading" ? <p className="console-loading" role="status" aria-live="polite">Loading booking data…</p> : null}
      {state.kind === "error" ? <p className="console-empty" role="status">Booking data is unavailable right now. Try refreshing in a moment.</p> : null}

      {state.kind === "ready" ? (
        <>
          {composerOpen ? (
            <BookingComposer
              properties={state.data.properties}
              onClose={() => setComposerOpen(false)}
              onBooked={() => { void refresh(); }}
            />
          ) : null}

          <div className="recon-actionbar" aria-label="Reservation actions">
            <button type="button" className="console-btn console-btn--ghost" disabled={expiring} onClick={() => void handleExpireHolds()}>
              {expiring ? "Sweeping…" : "Expire holds"}
            </button>
          </div>

          <ReservationsPanel
            reservations={state.data.reservations}
            onConfirm={(id) => void handleConfirm(id)}
            onCancel={(id) => void handleCancel(id)}
            actingId={actingId}
          />
        </>
      ) : null}
    </section>
  );
}
